import { useState } from 'react';
import { Sparkles, Loader } from 'lucide-react';
import { api } from '../../api/client';
import { useToast } from '../../store/toastStore';
import type { Pipeline } from '../../store/pipelineStore';

// Minimal typed shapes for what we need from intel data
interface TrendSnippet { timingAssessment: { verdict: string }; marketSize: { value: string | null }; growthRate: { value: string | null } }
interface CompetitorSnippet { competitors: Array<{ name: string; detectedStrengths: string[] }> }
interface KwSnippet { quickWins: Array<{ term: string }> }
interface SentimentSnippet { painPoints: Array<{ point: string }> }

type AnyStatus = 'idle' | 'loading' | 'done' | 'error';

export interface IntelSectionsForSynthesis {
  nameValidation: { status: AnyStatus; data: unknown };
  trends: { status: AnyStatus; data: unknown };
  competitors: { status: AnyStatus; data: unknown };
  keywords: { status: AnyStatus; data: unknown };
  sentiment: { status: AnyStatus; data: unknown };
}

interface Props {
  startupId: string;
  pipeline: Pipeline | null;
  intel: IntelSectionsForSynthesis;
  doneCount: number;
}

function buildSynthesisText(intel: IntelSectionsForSynthesis, pipeline: Pipeline | null): string {
  const parts: string[] = [];

  const trends = intel.trends.status === 'done' ? (intel.trends.data as TrendSnippet) : null;
  const competitors = intel.competitors.status === 'done' ? (intel.competitors.data as CompetitorSnippet) : null;
  const keywords = intel.keywords.status === 'done' ? (intel.keywords.data as KwSnippet) : null;
  const sentiment = intel.sentiment.status === 'done' ? (intel.sentiment.data as SentimentSnippet) : null;

  // Market timing
  if (trends?.timingAssessment?.verdict) {
    let segment = `${trends.timingAssessment.verdict} market timing`;
    if (trends.marketSize?.value) segment += ` in a ${trends.marketSize.value} market`;
    if (trends.growthRate?.value) segment += ` growing at ${trends.growthRate.value}`;
    parts.push(segment + '.');
  }

  // Top competitor
  const topCompetitor = competitors?.competitors?.[0];
  if (topCompetitor) {
    const strength = topCompetitor.detectedStrengths?.[0];
    let segment = `Your top competitor is ${topCompetitor.name}`;
    if (strength) segment += `, known for ${strength.toLowerCase()}`;
    parts.push(segment + '.');
  }

  // Differentiation from pipeline positioning
  const positioningOutput = pipeline?.positioning?.aiOutput as Record<string, unknown> | undefined;
  const uniqueMechanism = positioningOutput?.uniqueMechanism as string | undefined;
  const valueProposition = positioningOutput?.valueProposition as string | undefined;
  const diff = uniqueMechanism ?? valueProposition;
  if (diff) {
    parts.push(`Your differentiation window: ${diff}.`);
  }

  // Top keyword
  const topKw = keywords?.quickWins?.[0];
  if (topKw) {
    parts.push(`Biggest keyword opportunity: "${topKw.term}".`);
  }

  // Key pain point
  const topPain = sentiment?.painPoints?.[0];
  if (topPain) {
    parts.push(`Key customer pain: ${topPain.point.slice(0, 100)}${topPain.point.length > 100 ? '…' : ''}`);
  }

  if (parts.length === 0) {
    return 'Run more intelligence modules to generate a synthesis of your market position.';
  }

  return parts.join(' ');
}

export function SynthesisCard({ startupId, pipeline, intel, doneCount }: Props) {
  const { toast } = useToast();
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [fullSynthesis, setFullSynthesis] = useState<string | null>(null);

  if (doneCount < 2) return null;

  const synthesisText = buildSynthesisText(intel, pipeline);

  const handleGenerateFull = async () => {
    setIsSynthesizing(true);
    try {
      const { data } = await api.post(`/intelligence/${startupId}/synthesize`);
      setFullSynthesis(data.data?.synthesis ?? data.data?.text ?? null);
      toast('Full synthesis generated!', 'success');
    } catch (err: unknown) {
      // Endpoint not yet implemented — stub gracefully
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 501 || status === 503) {
        console.info('[StartupScore] POST /api/intelligence/:id/synthesize not yet implemented on backend — stub showing toast');
        toast('Synthesis generation coming soon — your AI analyst is being trained.', 'info');
      } else {
        toast('Failed to generate synthesis. Try again shortly.', 'error');
      }
    } finally {
      setIsSynthesizing(false);
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0d0f1a 0%, #161630 50%, #0f1020 100%)',
      border: '1px solid rgba(124,111,247,0.3)',
      borderRadius: 14, padding: '20px 24px', marginTop: 48,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
          background: 'rgba(124,111,247,0.15)', color: '#7c6ff7',
          border: '1px solid rgba(124,111,247,0.3)', letterSpacing: '0.05em',
        }} className="flex items-center gap-2"><Sparkles size={18} className="text-brand-500" /> AI Synthesis</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
          background: 'rgba(249,115,22,0.15)', color: '#f97316',
          border: '1px solid rgba(249,115,22,0.3)', letterSpacing: '0.05em',
        }}>COMING SOON</span>
        <span style={{ fontSize: 11, color: '#4b5563' }}>Based on {doneCount}/5 modules</span>
      </div>

      {/* Synthesis text */}
      <p style={{
        fontSize: 16, lineHeight: 1.7, color: 'rgba(200,195,255,0.9)',
        fontWeight: 400, maxWidth: 780,
      }}>
        {fullSynthesis ?? synthesisText}
      </p>

      {/* CTA */}
      {/* FIX: #3 — disable synthesis button until feature is live */}
      {!fullSynthesis && (() => {
        const isSynthesisReady = false; // flip to true when the feature ships
        return (
          <button
            onClick={isSynthesisReady ? handleGenerateFull : undefined}
            disabled={!isSynthesisReady || isSynthesizing}
            title={isSynthesisReady ? undefined : 'Full synthesis is coming soon — credits will not be charged until it\'s live'}
            style={{
              marginTop: 16,
              background: isSynthesizing ? '#2a2a3e' : 'linear-gradient(135deg, #7c6ff7, #6366f1)',
              color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 22px', fontSize: 14, fontWeight: 600,
              cursor: !isSynthesisReady ? 'not-allowed' : isSynthesizing ? 'default' : 'pointer',
              opacity: !isSynthesisReady ? 0.5 : 1,
              transition: 'opacity 0.2s',
              position: 'relative' as const,
            }}
          >
            {isSynthesizing ? <><Loader size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate Synthesis</>}
            {!isSynthesisReady && (
              <span style={{
                marginLeft: 8, fontSize: 10, fontWeight: 700,
                background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.4)',
                borderRadius: 4, padding: '2px 6px', color: '#f97316', verticalAlign: 'middle',
              }}>COMING SOON</span>
            )}
          </button>
        );
      })()}
    </div>
  );
}
